// The device command vocabulary, in one place.
//
// Sourced from the working MQTT console (mqttbox-reactjs MainPage.js) and the
// Flutter apps, so every screen sends the same literal strings.
//
// ── CASE RULE, straight from the firmware ──────────────────────────────────
//
//   if (!tempword.startsWith("REMOVEWIFI") && !tempword.startsWith("ADDWIFI")) {
//       tempword.toUpperCase();
//   }
//
// Everything EXCEPT the wifi commands is upper-cased before matching, so case is
// irrelevant for them and the house style is ALL LOWERCASE.
//
// ⚠️ ADDWIFI / REMOVEWIFI ARE DELIBERATELY SKIPPED by that upper-casing, because
// the payload carries an SSID and a password and those ARE case-sensitive —
// upper-casing them would corrupt the credentials. So these take no change of
// any kind, not case and not spacing. Exact literals:
//
//     ADDWIFIssid=<ssid>pass=<password>        (MQTT, no separators at all)
//     REMOVEWIFIssid=<ssid>pass=<password>     (MQTT, no separators at all)
//     ADDWIFI ssid=<ssid>&pass=<password>      (BLE — space and '&', different!)
//
// The MQTT and BLE provisioning payloads are NOT interchangeable: MQTT runs the
// fields together, BLE separates them with a space and an '&'. Reusing one for
// the other silently fails to parse.

// ── Simple word commands (lowercase) ───────────────────────────────────────
export const CMD_ALL_ON = 'allon';
export const CMD_ALL_OFF = 'alloff';
export const CMD_S05 = 's05';
// The firmware accepts UPDATECODE / UPDATE CODE / CODEUPDATE / CODE UPDATE, so
// the space is optional. This spelling matches the MQTT console's presets.
export const CMD_UPDATE_CODE = 'updatecode';
export const CMD_GET_ALL_WIFI = 'getallwifi';
export const CMD_DEBUG_ON = 'debugon';
export const CMD_DEBUG_OFF = 'debugoff';

// ── Child lock — locks the physical keypad, leaving the app in control ─────
//
// The firmware turns these words into a Tuya DP write on the serial link to the
// MCU. The APP NEVER CHOOSES THE DP; see core/devices/childLock.js for which one
// it currently picks and why that matters.
export const CMD_CHILD_LOCK_ON = 'CHILDLOCKON';
export const CMD_CHILD_LOCK_OFF = 'CHILDLOCKOFF';

// Shown as shortcut buttons in the message console, in this order.
export const PRESET_COMMANDS = [
  CMD_ALL_ON, CMD_ALL_OFF, CMD_S05, CMD_UPDATE_CODE, CMD_GET_ALL_WIFI,
];
export const DEBUG_COMMANDS = [CMD_DEBUG_ON, CMD_DEBUG_OFF];

// ── Wi-Fi commands — EXACT LITERALS, do not normalise ──────────────────────
//
// ⚠️ THESE TWO ARE PUBLISHED WITHOUT THE `#<userId>` SUFFIX — `publishAddressed`
// in core/utils/deviceController, never `publishCommand`. Everything else goes
// out as `[<uno>]<message>#<userId>`; these go out as `[<uno>]<message>`.
//
// The ESP8266 firmware deliberately does NOT strip the `#…` for these two verbs,
// because a Wi-Fi password may legitimately contain a '#':
//
//     // OtaForSwitchingNewDesign.ino, MessageFromMqtt()
//     if (MqttMessage.indexOf("ADDWIFI") < 0 && MqttMessage.indexOf("REMOVEWIFI") < 0) {
//         ...strip everything from '#'...
//     }
//
// so the handler's `substring(indexOf("pass=") + 5)` swallows the user id and
// the password becomes `<pass>#123`. `SMARTPicker::removeSsid` matches the ssid
// AND the password exactly, so nothing was ever erased — while the device
// rebooted regardless, which is precisely why this read as "REMOVEWIFI does
// nothing". ADDWIFI stored an unjoinable password the same way. Both apps have
// sent it wrapped since the beginning; it has never worked over MQTT on that
// fleet.
//
// The `[<uno>]` prefix STAYS. Without it the ESP32 firmware treats the command
// as a broadcast and every device on the topic acts on it (OTA_CLAUDE.md §5.1).
export const buildAddWifiMqtt = (ssid, password) =>
  `ADDWIFIssid=${ssid}pass=${password}`;

export const buildRemoveWifiMqtt = (ssid, password) =>
  `REMOVEWIFIssid=${ssid}pass=${password}`;

// Networks the firmware falls back to. Removing one can strand a device with no
// way back onto the network, so every remove path refuses them.
//
// On the ESP32 fleet removing the last stored network cannot work anyway: an
// empty list is reseeded with exactly these on the next boot
// (ESP8685WROOM04C/main/main.c — "REMOVEWIFI does not survive a reboot").
export const PROTECTED_SSIDS = ['home', 'smartify'];

export const isProtectedSsid = (ssid) =>
  PROTECTED_SSIDS.includes(String(ssid || '').trim().toLowerCase());

// ── Reading what a device says back ────────────────────────────────────────

/** The ESP32 firmware's word for "no networks stored". */
const EMPTY_REPLY = 'EMPTY';

// The `[E072A14582D0]` a device wraps a reply in — optional, since the ESP32
// publishes these particular replies bare.
//
// ⚠️ ANCHORED ON WORD CHARACTERS, DELIBERATELY. The obvious `/^\[[^\]]*\]/` also
// matches the opening of the ESP32's JSON reply — `[["Home","Automation"]` — and
// silently eats the first network. A unique number has no quotes or commas in
// it, so requiring `[\w-]` cannot match JSON.
const ENVELOPE = /^\[[\w-]{1,32}\]\s*/;

// A relay status frame — `0,1,0,0#Touch`, `1-0#Mqtt`, `10` — arrives on the very
// same `<uno>/client` topic, constantly, and its comma-separated shape also fits
// the ESP8266's `ssid,pass`. Without this it parses as a network called "0" with
// the password "1,0,0#Touch", and the saved-networks list fills with them.
//
// The old parser rejected these by accident — it demanded exactly two fields, so
// a four-relay frame fell out. That same rule is what dropped every real reply
// with a comma in the password, so the test has to be deliberate instead.
//
// Digits, commas, dashes and spaces only, plus an optional `#source` tail. The
// cost is narrow and stated: a network whose SSID *and* password are both purely
// numeric reads as a status frame. Nothing else does.
const STATUS_FRAME = /^[\d\s,-]+(#\S*)?$/;

// Split `text` on `outer` and keep the chunks only if EVERY one of them carries
// the `inner` separator. That "every" is what tells the two CSV dialects apart
// without guessing from a single character appearing somewhere in the string.
function chunksOf(text, outer, inner) {
  const chunks = text.split(outer).map((c) => c.trim()).filter(Boolean);
  return chunks.length && chunks.every((c) => c.includes(inner)) ? chunks : null;
}

// One "a<sep>b" pair. Splits on the FIRST separator only, so a password
// containing one survives — the old parser demanded exactly two fields and
// dropped the whole entry when it found three.
const pairOf = (chunk, sep) => {
  const at = chunk.indexOf(sep);
  const ssid = (at === -1 ? chunk : chunk.slice(0, at)).trim();
  return { ssid, pass: at === -1 ? '' : chunk.slice(at + 1).trim() };
};

/**
 * The reply to `getallwifi` -> [{ ssid, pass }].
 *
 * ⚠️ THREE WIRE FORMATS, because the two silicon families answer differently
 * and the ESP32 answers twice. Reading only the ESP8266 shape — which is all
 * this did — turned a C2/C3 reply into garbage that still LOOKED like a list:
 *
 *   ESP8266   `Home,Automation;Smartify,Smartify;`   (one network: no ';' at
 *             all, the handler chops the trailing separator)
 *   ESP32     `Home:Automation,Smartify:Smartify`    then, as a SECOND message,
 *             `[["Home","Automation"],["Smartify","Smartify"]]`
 *   ESP32     `EMPTY`                                nothing stored
 *
 * With two networks stored, the old parser read the ESP32's CSV as ONE pair —
 * ssid `Home:Automation`, password `Smartify:Smartify` — and a REMOVEWIFI aimed
 * at that SSID could only ever answer NOTFOUND. With one or three it returned
 * nothing and the device read as offline.
 *
 * The ESP32 shape is tested FIRST: its ':' is the specific marker, and an
 * ESP8266 reply with two networks also contains a ',' inside a ';' chunk.
 */
export function parseWifiList(payload) {
  const body = String(payload || '').replace(ENVELOPE, '').trim();
  if (!body || body.toUpperCase() === EMPTY_REPLY) return [];
  if (STATUS_FRAME.test(body)) return [];

  // The JSON second reply. Anything starting '[' is JSON or nothing — no CSV
  // dialect begins with a bracket once the envelope is off.
  if (body.startsWith('[')) {
    try {
      const arr = JSON.parse(body);
      if (!Array.isArray(arr)) return [];
      return arr
        .map((e) => (Array.isArray(e)
          ? { ssid: String(e[0] ?? '').trim(), pass: String(e[1] ?? '').trim() }
          : { ssid: String(e?.ssid ?? '').trim(), pass: String(e?.pass ?? '').trim() }))
        .filter((w) => w.ssid);
    } catch (e) {
      return [];
    }
  }

  const esp32 = chunksOf(body, ',', ':');
  if (esp32) return esp32.map((c) => pairOf(c, ':')).filter((w) => w.ssid);

  const esp8266 = chunksOf(body, ';', ',');
  if (esp8266) return esp8266.map((c) => pairOf(c, ',')).filter((w) => w.ssid);

  return [];
}

// ── ADDWIFI / REMOVEWIFI acknowledgements ──────────────────────────────────

export const WIFI_ACK_OK = 'ok';
export const WIFI_ACK_NOT_FOUND = 'notfound';
export const WIFI_ACK_ERROR = 'error';
/** The ESP8266's ack — sent BEFORE it tries, so it is receipt, not proof. */
export const WIFI_ACK_ACCEPTED = 'accepted';

/**
 * What a device said about an ADDWIFI / REMOVEWIFI -> { verb, result, ssid, text }
 * or null when this message is something else.
 *
 * Both families answer and neither answer was being read, which is why a
 * removal that failed and one that worked produced the same screen:
 *
 *   ESP32     `REMOVEWIFI OK ssid=Home` · `REMOVEWIFI NOTFOUND ssid=Home`
 *             `REMOVEWIFI ERROR ssid=Home err=…` · `REMOVEWIFI parse failed`
 *   ESP8266   `Wifi-Home,Pass-Automation Sent for Deletion`
 *
 * ⚠️ A SUCCESSFUL REMOVE REBOOTS THE DEVICE on both families, so the ack is the
 * last thing it says for ten seconds or so. Anything polling sooner than that
 * finds silence and must not report it as a failure.
 */
export function parseWifiAck(payload) {
  const text = String(payload || '').replace(ENVELOPE, '').trim();

  const esp32 = text.match(/^(ADDWIFI|REMOVEWIFI)\s+(OK|NOTFOUND|ERROR|parse\s+failed)\b/i);
  if (esp32) {
    const word = esp32[2].toLowerCase().replace(/\s+/g, ' ');
    const result = word === 'ok' ? WIFI_ACK_OK
      : word === 'notfound' ? WIFI_ACK_NOT_FOUND
        : WIFI_ACK_ERROR;
    return {
      verb: esp32[1].toUpperCase(),
      result,
      ssid: (text.match(/\bssid=([^\s]*)/i) || [])[1] || '',
      text,
    };
  }

  const esp8266 = text.match(/^Wifi-(.*),Pass-.*\bSent for (Deletion|Adding New Wifi)\b/i);
  if (esp8266) {
    return {
      verb: /Deletion/i.test(esp8266[2]) ? 'REMOVEWIFI' : 'ADDWIFI',
      result: WIFI_ACK_ACCEPTED,
      ssid: esp8266[1].trim(),
      text,
    };
  }

  return null;
}

/**
 * How long to wait before asking a device what it holds now.
 *
 * Both firmwares `ESP.restart()` / `esp_restart()` immediately after a
 * successful add or remove, so the answer to "did it work" only exists once the
 * device is back on the broker. Measured at ~6-9 s on a C3; this is that with
 * room to spare. The old 1.8 s re-list could not have worked even in principle.
 */
export const WIFI_REBOOT_SETTLE_MS = 14000;

// ── Topic ──────────────────────────────────────────────────────────────────
// A bare unique number means the device's command topic.
export const toDeviceTopic = (topicOrUno) => {
  const t = String(topicOrUno || '').trim();
  return t.includes('/') ? t : `${t}/device`;
};
